import { Locale } from 'next-intl'
import { v4 as uuidv4 } from 'uuid'

import {
  type TLocationMarkerType,
  TSubwayLine,
  ZLocationMarkerType,
} from '@/entities/map'
import { formatDate, StringDateRange } from '@/shared/lib'

import { TLocation } from '../selected-location'

export type History = {
  id: string
  name: string
  keyword: string
  type: TLocationMarkerType | null
  nextZoomLevel: number | null
  location: {
    lat: number
    lng: number
  } | null
  startDate: string
  endDate: string
  subwayLines: TSubwayLine[] | null
  child: {
    type: 'badge' | 'icon'
    name: string
    shortName: string
    color: string
  }[]
}

const LANDMARK_CHILD: History['child'] = [
  {
    type: 'icon',
    name: 'flag',
    shortName: 'flag',
    color: 'text-blue-500',
  },
]

const SUBWAY_CHILD = (subwayLines: TSubwayLine[] | null) => {
  if (!subwayLines) {
    return []
  }

  return subwayLines.map(({ name, color, displayName }) => ({
    type: 'badge',
    name,
    shortName: displayName,
    color,
  })) as History['child']
}

const DEFAULT_LOCATION_FIELDS = {
  name: '',
  keyword: '',
  type: null,
  nextZoomLevel: null,
  location: null,
  subwayLines: null,
  child: [],
}

const getLocationFields = (location: TLocation | null) => {
  if (!location) {
    return DEFAULT_LOCATION_FIELDS
  }

  return {
    name: location.name,
    keyword: location.keyword,
    type: location.type,
    nextZoomLevel: location.nextZoomLevel,
    location: {
      lat: location.lat,
      lng: location.lng,
    },
    subwayLines: location.subwayLines,
    child: (() => {
      if (location.type === ZLocationMarkerType.Enum.LANDMARK) {
        return LANDMARK_CHILD
      }

      if (location.type === ZLocationMarkerType.Enum.SUBWAY) {
        return SUBWAY_CHILD(location?.subwayLines ?? null)
      }

      return []
    })(),
  }
}

const DEFAULT_DATE_RANGE_FIELDS = {
  startDate: '',
  endDate: '',
}

const getDateRangeFields = ({
  dateRange,
  locale,
}: {
  dateRange?: StringDateRange
  locale: Locale
}) => {
  if (!dateRange) {
    return DEFAULT_DATE_RANGE_FIELDS
  }

  return {
    startDate: formatDate({
      date: dateRange.from,
      format: 'yyyy-MM-dd',
      locale,
    }),
    endDate: formatDate({ date: dateRange.to, format: 'yyyy-MM-dd', locale }),
  }
}

export const convertToSearchHistory = ({
  location,
  dateRange,
  locale,
}: {
  location: TLocation | null
  dateRange?: StringDateRange
  locale: Locale
}): History => {
  return {
    id: uuidv4(),
    ...getLocationFields(location),
    ...getDateRangeFields({ dateRange, locale }),
  }
}
